import React, { useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

export default function CrmPresentationModal({ isOpen, onClose }) {
  const backdropRef = useRef(null);
  const closingRef = useRef(false);

  const handleClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    const el = backdropRef.current;
    if (el) {
      el.style.transition = 'opacity 0.3s ease-out';
      el.style.opacity = '0';
      setTimeout(() => { closingRef.current = false; onClose(); }, 300);
    } else { closingRef.current = false; onClose(); }
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    // Simply hide overflow - no position:fixed, no scroll manipulation
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    const onMsg = (e) => { if (e.origin !== window.location.origin) return; if (e.data && e.data.type === 'crm-close') handleClose(); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('message', onMsg);
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('message', onMsg);
    };
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (!isOpen) return;
    const el = backdropRef.current;
    if (el) {
      el.style.opacity = '0';
      requestAnimationFrame(() => requestAnimationFrame(() => {
        el.style.transition = 'opacity 0.35s cubic-bezier(0.4,0,0.2,1)';
        el.style.opacity = '1';
      }));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const modal = (
    <>
      <style>{`
        .crm-popup-backdrop {
          position:fixed; top:0; left:0; width:100vw; height:100vh; height:100dvh;
          z-index:2147483647; display:flex; align-items:center; justify-content:center;
          background:rgba(0,0,0,0.92); backdrop-filter:blur(14px); -webkit-backdrop-filter:blur(14px);
          cursor:pointer; padding:20px;
        }
        .crm-popup-container {
          position:relative; width:92vw; height:82vh; max-width:1200px; max-height:82vh; cursor:default;
        }
        .crm-popup-close {
          position:absolute; top:-44px; right:0; z-index:10;
          width:36px; height:36px; border-radius:50%;
          background:rgba(255,255,255,0.08); border:1.5px solid rgba(255,255,255,0.2);
          display:flex; align-items:center; justify-content:center;
          cursor:pointer; color:rgba(255,255,255,0.7); padding:0; outline:none;
          transition:all 0.25s cubic-bezier(0.4,0,0.2,1);
        }
        .crm-popup-close:hover {
          background:rgba(255,255,255,0.2); color:#fff;
          transform:scale(1.1) rotate(90deg);
        }
        .crm-popup-frame {
          width:100%; height:100%; border-radius:14px; overflow:hidden;
          border:1px solid rgba(102,126,234,0.2);
          box-shadow:0 0 60px rgba(102,126,234,0.1),0 20px 80px rgba(0,0,0,0.6);
          background:#06080f; position:relative;
        }
        .crm-popup-glow {
          position:absolute; top:0; left:10%; right:10%; height:2px;
          background:linear-gradient(90deg,transparent,rgba(102,126,234,0.5),rgba(6,214,160,0.5),transparent);
          z-index:2; pointer-events:none;
        }
        .crm-popup-frame iframe { width:100%; height:100%; border:none; display:block; }

        @media (max-width:1024px) {
          .crm-popup-backdrop { padding:14px; }
          .crm-popup-container { width:96vw; height:84vh; max-height:84vh; }
          .crm-popup-close { top:-40px; width:34px; height:34px; }
        }
        @media (max-width:768px) {
          .crm-popup-backdrop { padding:0; }
          .crm-popup-container { width:100vw; height:100vh; height:100dvh; max-width:100%; max-height:100vh; max-height:100dvh; }
          .crm-popup-frame { border-radius:0; border:none; box-shadow:none; }
          .crm-popup-glow { display:none; }
          .crm-popup-close {
            top:auto; right:auto; bottom:calc(100% + 0px); left:auto;
            position:absolute; top:12px; right:12px;
            width:40px; height:40px;
            background:rgba(0,0,0,0.6); border-color:rgba(255,255,255,0.15);
            box-shadow:0 2px 12px rgba(0,0,0,0.5);
          }
        }
        @supports (padding: env(safe-area-inset-top)) {
          @media (max-width:768px) {
            .crm-popup-close { top:max(12px, env(safe-area-inset-top)); right:max(12px, env(safe-area-inset-right)); }
          }
        }
      `}</style>

      <div ref={backdropRef} className="crm-popup-backdrop"
        onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        onTouchEnd={(e) => { if (e.target === e.currentTarget) { e.preventDefault(); handleClose(); } }}
      >
        <div className="crm-popup-container">
          <button className="crm-popup-close" onClick={handleClose}
            onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleClose(); }}
            aria-label="Kapat"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/>
            </svg>
          </button>

          <div className="crm-popup-frame">
            <div className="crm-popup-glow" />
            <iframe
              src="/crm-tanitim.html"
              title="WhowSell CRM Tanıtım"
              allow="autoplay"
              sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
            />
          </div>
        </div>
      </div>
    </>
  );

  // Portal: render directly into document.body so it's independent of any section/scroll position
  return ReactDOM.createPortal(modal, document.body);
}
